"use client";

import React, { useCallback, useMemo } from 'react';
import { GridActionsCellItem, GridColDef, } from '@mui/x-data-grid-pro';
import { MediaState, Segment } from '@/types/types.ts';
import useMediaState from "@/common/hooks/useMediaState.ts";
import { ArrowDropDown, FiberManualRecord } from "@mui/icons-material";
import secondsToString from "@/utils/secondsToString.ts";
import useCurrentExplorerItem from "@/common/hooks/useCurrentExplorerItem.ts";
import { ContextMenuTypes } from "@/enum/contextMenuTypes.ts";
import useCurrentContextMenu from "@/common/hooks/useCurrentContextMenu.ts";
import { Checkbox, Tooltip } from "@mui/material";
import { PlaymodeType } from "@/enum/playmodeType.ts";
import {
  SegmentColorCell
} from "@/features/AudioWorkstation/components/AudioEditorTabs/EditorTabPane/SegmentColorCell.tsx";
import { ButtonWithIcon } from "@/common/components/Button/ButtonWithIcon.tsx";
import PauseIcon from "@mui/icons-material/PauseRounded";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import Typography from "@mui/material/Typography";
import RecordingSegmentName
  from "@/features/AudioWorkstation/components/AudioEditorTabs/EditorTabPane/RecordingSegmentName.tsx";
import dateTimeToString from "@/utils/dateTimeToString.ts";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForever from "@mui/icons-material/DeleteForever";
import { deleteSegment } from "@/services/api/track.ts";
import useAudioAdapter from "@/common/hooks/useAudioAdapter.ts";
import useUniqueTabId from "@/common/hooks/useUniqueTabId.ts";
import usePlayingSegment from "@/common/hooks/usePlayingSegment.ts";
import { useAudioPlayback } from "@/common/hooks/useAudioPlayback.ts";
import useRecordingSegment from "@/common/hooks/useRecordingSegment.ts";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import useCurrentExplorerItemId from "@/common/hooks/useCurrentExplorerItemId.ts";
import { useModalQueryState } from "@/common/hooks/nuqs/useModalQueryState.ts";
import { useMutateExplorerItems } from "@/state/jotai/hooks/useMutateExplorerItems.ts";
import { createConfirmAction, openConfirmDialog } from "@/utils/utils.ts";
import { updateSegment } from "@/services/api/segment.ts";

export const EditorPanelColumns = () => {
  const [audioAdapter] = useAudioAdapter();
  const currentExplorerItem = useCurrentExplorerItem();
  const currentTrack = currentExplorerItem?.track;
  const segments = currentTrack?.segments;
  const [mediaState] = useMediaState();
  const [uniqueTabId] = useUniqueTabId();
  const [playingSegment] = usePlayingSegment();
  const { play, pause, seek } = useAudioPlayback();
  const [, setCurrentContextMenu] = useCurrentContextMenu();
  const [recordingSegment, setRecordingSegment] = useRecordingSegment();
  const isPlaying = mediaState === MediaState.PLAYING;
  const [playmode, setPlaymodeQueryString] = useSearchParamState(URLSearchParamKeys.PLAY_MODE);

  const [currentExplorerItemId] = useCurrentExplorerItemId()
  const { setModalParam: setEditSegmentModalParam } = useModalQueryState(URLSearchParamKeys.EDIT_SEGMENT_MODAL);

  const {
    updateTrackSegment,
    deleteTrackSegment,
  } = useMutateExplorerItems();


  const handleEditSegment = useCallback((segment: Segment) => {
    if (!segment?.id) {
      console.log('Warning: segment not defined @ EditorTabPane.handleEditSegment')
      return;
    }

    setEditSegmentModalParam(segment.id)
  }, [
    setEditSegmentModalParam
  ])

  const handleOpenSegmentOptionsMenu = useCallback((mouseEvent: React.MouseEvent, segment: Segment) => {
    setCurrentContextMenu({
      type: ContextMenuTypes.TABLE_ROW_SEGMENT,
      data: {
        segment,
        mouseEvent,
      }
    })
  }, [setCurrentContextMenu])

  const handleTogglePlaySegmentMode = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setPlaymodeQueryString(PlaymodeType.SEGMENTS)
    } else {
      setPlaymodeQueryString(PlaymodeType.PLAYLIST)
    }
  }, [setPlaymodeQueryString]);

  const replaySegment = useCallback((segment: Segment) => {
    if (!currentExplorerItem) {
      console.log('Warning: currentExplorerItem is not defined @ SegmentTableRow.replaySegment')
      return
    }

    seek(segment.startTime)
    play()
  }, [
    currentExplorerItem,
    play,
    seek
  ])

  const playSegment = useCallback((segment: Segment) => {
    if (currentExplorerItem && playingSegment && playingSegment.id === segment.id) {
      play(currentExplorerItem)
      return
    }

    replaySegment(segment)
  }, [
    currentExplorerItem,
    play,
    playingSegment,
    replaySegment
  ]);

  const pausePlayer = useCallback(() => {
    pause()
  }, [pause])

  const handleSegmentColorChange = useCallback((id: string, color: string) => {
    if (!currentExplorerItemId || !segments) {
      return;
    }

    const segment = segments[id];
    const updatedSegment = {
      ...segment,
      color
    }

    updateTrackSegment(currentExplorerItemId, updatedSegment)

    updateSegment(
      currentExplorerItemId,
      updatedSegment,
      uniqueTabId,
    )
  }, [
    currentExplorerItemId,
    segments,
    uniqueTabId,
    updateTrackSegment
  ])

  const doDeleteSegment = useCallback((segment: Segment) => {
    if (!currentTrack?.id) {
      console.log('Warning: currentTrack is not defined (@SegmentTableRow.handleDeleteSegment)')
      return
    }

    deleteTrackSegment(currentTrack.explorerItemId, segment)

    deleteSegment(
      currentTrack.explorerItemId,
      String(segment.id),
      uniqueTabId,
    )
  }, [
    currentTrack?.explorerItemId,
    currentTrack?.id,
    deleteTrackSegment,
    uniqueTabId
  ])

  const handleDeleteSegment = useCallback((segment: Segment) => {
    openConfirmDialog({
      title: "Delete selection",
      message: "Are you sure you want to delete this selection?",
      onConfirm: createConfirmAction(doDeleteSegment, segment)
    })
  }, [doDeleteSegment])

  const handleStopRecordSegment = useCallback(() => {
    if (!currentTrack || !recordingSegment) {
      return
    }
    setRecordingSegment(null)

    updateSegment(
      currentTrack.explorerItemId,
      {
        ...recordingSegment,
        endTime: audioAdapter?.getCurrentTime() || 0
      },
      uniqueTabId,
    )
  }, [
    audioAdapter,
    currentTrack,
    recordingSegment,
    setRecordingSegment,
    uniqueTabId
  ])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'playBack',
      headerName: '',
      width: 60,
      sortable: false,
      hideable: false,
      align: 'right',
      filterable: false,
      disableColumnMenu: true,
      // cellClassName: styles.FolderOrFileIndicatorIcon,
      renderHeader: () => (
        <Tooltip title="Play selections" placement={"top"}>
          <Checkbox
            disabled={!!recordingSegment}
            checked={playmode === PlaymodeType.SEGMENTS}
            onKeyDown={handleKeyDown}
            onChange={handleTogglePlaySegmentMode}
            sx={{
              transform: 'scale(0.8)',
              opacity: 0.7,
              transition: 'opacity 0.3s ease',
              '&:hover': {
                opacity: 1,
              }
            }}
          />
        </Tooltip>
      ),
      renderCell: (params) => {
        const segment = params.row as Segment

        return (
          <>
            <SegmentColorCell
              segment={segment}
              onChangeColor={handleSegmentColorChange}
            />
            {
              recordingSegment?.id === segment.id
                ?
                <ButtonWithIcon
                  size={"small"}
                  icon={<FiberManualRecord fontSize="small" color={"error"} />}
                  onClick={() => {
                    handleStopRecordSegment()
                  }}
                />
                :
                <ButtonWithIcon
                  size={"small"}
                  disabled={!!recordingSegment}
                  icon={
                    !recordingSegment && isPlaying && playingSegment?.id === segment.id ? (
                      <PauseIcon color={"primary"} fontSize={"medium"} />
                    ) : (
                      <PlayIcon fontSize={"medium"} />
                    )
                  }
                  onClick={() => {
                    if (playingSegment?.id === segment.id) {
                      pausePlayer()
                    } else {
                      playSegment(segment)
                    }
                  }}
                />
            }
          </>
        )
      },
    },
    {
      field: 'labelText',
      headerName: 'Name',
      flex: 1,
      editable: true,
      sortable: true,
      pinnable: false,
      renderCell: (params) => {
        if (recordingSegment?.id === params.id) {
          return (
            <Typography sx={{ my: 1 }}>
              <RecordingSegmentName />
            </Typography>
          )
        }

        const index = params.api.getRowIndexRelativeToVisibleRows(params.id);

        return (
          <div>
            {
              params.value || `Selection #${(index || 0) + 1}`
            }
          </div>
        );
      },
    },
    {
      field: 'duration',
      headerName: 'Duration',
      flex: 1,
      editable: false,
      sortable: true,
      pinnable: false,
      sortComparator: (_v1, _v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id);
        const row2 = param2.api.getRow(param2.id);
        const duration1 = row1.endTime - row1.startTime;
        const duration2 = row2.endTime - row2.startTime;
        return duration1 - duration2;
      },
      renderCell: (params) => {
        const duration = params.row.endTime - params.row.startTime;
        return (
          <div>{secondsToString(duration)}</div>
        );
      },
    },
    {
      field: 'startTime',
      headerName: 'Start time',
      flex: 1,
      editable: false,
      sortable: true,
      renderCell: (params) => {
        return (
          <div>{secondsToString(params.row.startTime)}</div>
        );
      },
    },
    {
      field: 'endTime',
      headerName: 'End time',
      flex: 1,
      editable: false,
      sortable: true,
      renderCell: (params) => {
        return (
          <div>{secondsToString(params.row.endTime)}</div>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 1,
      renderCell: (params) => params.value ? dateTimeToString(params.value) : null
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      flex: 1,
      renderCell: (params) => params.value ? dateTimeToString(params.value) : null
    },
    {
      display: 'flex',
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 150,
      resizable: false,
      align: 'right',
      cellClassName: 'actions',
      getActions: (params) => {
        const { row } = params;
        const segment = row as Segment
        if (recordingSegment?.id === segment.id) {
          return [
            <GridActionsCellItem
              key="Edit"
              label="Edit"
              icon={<EditIcon />}
              color="inherit"
              onClick={() => handleEditSegment(segment)}
            />,
          ]
        }

        return [
          <GridActionsCellItem
            key="Edit"
            label="Edit"
            icon={<EditIcon />}
            color="inherit"
            onClick={() => handleEditSegment(segment)}
            disabled={!!recordingSegment}
          />,
          <GridActionsCellItem
            key="Delete"
            label="Delete"
            icon={<DeleteForever />}
            color="inherit"
            onClick={() => handleDeleteSegment(segment)}
            disabled={!!recordingSegment}
          />,
          <GridActionsCellItem
            key="Options"
            label="Options"
            icon={<ArrowDropDown />}
            onClick={e => handleOpenSegmentOptionsMenu(e, segment)}
            onClickCapture={e => handleOpenSegmentOptionsMenu(e, segment)}
            disabled={!!recordingSegment}
          />
        ];
      },
    },
  ], [
    recordingSegment,
    playmode,
    handleTogglePlaySegmentMode,
    handleSegmentColorChange,
    isPlaying,
    playingSegment?.id,
    handleStopRecordSegment,
    pausePlayer,
    playSegment,
    handleEditSegment,
    handleDeleteSegment,
    handleOpenSegmentOptionsMenu
  ]);

  return columns;
};
